<template>
    <div :class="componentClasses">
        <slot />
    </div>
</template>

<script setup lang="ts">
    import useWindowSize from '@/composables/useWindowSize';
    import { computed } from 'vue';
    import { Position, Variant } from '@keyvue/one-api-client';
    import useWidgetAppearance from '@/composables/useWidgetAppearance';

    const { isMobile } = useWindowSize();
    const { variantForIcon, positionForIcon } = useWidgetAppearance();

    const componentClasses = computed(() => {
        return {
            'chat-container': true,
            'chat-container--mobile': isMobile.value,
            'chat-container--desktop': !isMobile.value,
            'chat-container--in-iframe': window !== window.top,
            'chat-container--square': !Variant.SQUARE.equals(variantForIcon.value),
            'chat-container--rectangle': Variant.SQUARE.equals(variantForIcon.value),
            'chat-container--left': Position.LEFT.equals(positionForIcon.value),
            'chat-container--right': Position.RIGHT.equals(positionForIcon.value),
            'chat-container--top-left': Position.TOP_LEFT.equals(positionForIcon.value),
            'chat-container--top-right': Position.TOP_RIGHT.equals(positionForIcon.value),
            'chat-container--bottom-left': Position.BOTTOM_LEFT.equals(positionForIcon.value),
            'chat-container--bottom-right': Position.BOTTOM_RIGHT.equals(positionForIcon.value),
        };
    });
</script>

<style scoped lang="scss">
    .chat-container {
        height: 100%;
        width: 100%;
        display: grid;
        grid-template-rows: min-content 1fr min-content;
        background-color: white;

        &--mobile {
            position: fixed;
            inset: 0;
        }

        &--desktop {
            position: fixed;
            inset: 1rem;
            max-height: 80vh;
            max-width: min(40vw, 400px);
            box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
            border-radius: 10px;
            overflow: hidden;

            &.chat-container--square {
                &.chat-container--left {
                    top: auto;
                    right: auto;
                    left: 8rem;
                }

                &.chat-container--bottom-left {
                    top: auto;
                    right: auto;
                    bottom: 8rem;
                }

                &.chat-container--top-left {
                    bottom: auto;
                    right: auto;
                    top: 8rem;
                }

                &.chat-container--right {
                    top: auto;
                    left: auto;
                    right: 8rem;
                }

                &.chat-container--bottom-right {
                    top: auto;
                    left: auto;
                    bottom: 8rem;
                }

                &.chat-container--top-right {
                    bottom: auto;
                    left: auto;
                    top: 8rem;
                }
            }

            &.chat-container--rectangle {
                &.chat-container--left {
                    top: auto;
                    right: auto;
                    left: 5rem;
                }

                &.chat-container--bottom-left {
                    top: auto;
                    right: auto;
                    bottom: 5rem;
                }

                &.chat-container--top-left {
                    bottom: auto;
                    right: auto;
                    left: 5rem;
                }

                &.chat-container--right {
                    top: auto;
                    left: auto;
                    right: 5rem;
                }

                &.chat-container--bottom-right {
                    top: auto;
                    left: auto;
                    bottom: 5rem;
                }

                &.chat-container--top-right {
                    bottom: auto;
                    left: auto;
                    right: 5rem;
                }
            }
        }

        &--in-iframe {
            max-width: unset;
            inset: 0;
        }
    }

    @media screen and (min-width: 1600px) {
        .chat-container--desktop {
            max-width: min(40vw, 500px);
        }
    }
</style>
